import { useState, useRef, useEffect, type ReactNode } from "react";
import { useAuthContext, type AuthUser } from "../auth/AuthContext";
import { APP_CONFIG } from "../config/app.config";
import { ROLE_LABELS, type UserRole } from "../auth/roles";

export interface NavItem { id:string; label:string; icon:ReactNode; badge?:number; }
interface TopNavProps {
  items:NavItem[]; activeId:string; onNavigate:(id:string)=>void; onProfile:()=>void;
  maintenanceBadge?:boolean; onMaintenanceBadgeClick?:()=>void;
}

function Avatar({ user, size=34 }: { user:AuthUser; size?:number }) {
  const initials = user.name.split(" ").map(w=>w[0]).slice(0,2).join("").toUpperCase();
  if (user.avatar) return <img src={user.avatar} alt={user.name} style={{ width:size, height:size, borderRadius:"50%", objectFit:"cover", border:"2px solid var(--green)" }}/>;
  return (
    <div style={{ width:size, height:size, borderRadius:"50%", background:"#0078d4", border:"2px solid var(--green)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:size*0.36, color:"#fff", flexShrink:0 }} className="t-title">
      {initials}
    </div>
  );
}

/** Pill affichant le rôle effectif — orange si override actif */
function RoleBadge({ role, isOverride }: { role: UserRole; isOverride: boolean }) {
  const bg = isOverride ? "var(--gold)" : "var(--green)";
  const color = isOverride ? "#1a1a2e" : "var(--black)";
  return (
    <span style={{ background:bg, color, fontSize:9, fontWeight:700, letterSpacing:1,
      textTransform:"uppercase", padding:"2px 6px", borderRadius:3, marginLeft:4 }}
      className="t-title" title={isOverride ? "Rôle simulé pour cette session" : undefined}>
      {isOverride && "⚡ "}{ROLE_LABELS[role]}
    </span>
  );
}

// Rôles disponibles pour la simulation (admin peut simuler les rôles inférieurs)
const SWITCHABLE_ROLES: UserRole[] = ["admin", "manager", "lecteur"];

export default function TopNav({ items, activeId, onNavigate, onProfile, maintenanceBadge, onMaintenanceBadgeClick }: TopNavProps) {
  const { user, role, baseRole, overrideRole, logout } = useAuthContext();
  const [dropOpen, setDropOpen] = useState(false);
  const dropRef = useRef<HTMLDivElement>(null);

  const canSwitchRole = baseRole === "admin" || baseRole === "super-admin";
  const isOverride = role !== baseRole;

  useEffect(() => {
    const h = (e:MouseEvent) => { if(dropRef.current && !dropRef.current.contains(e.target as Node)) setDropOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  return (
    <nav className="navbar">
      {/* Logo */}
      <div className="shrink-0" style={{ marginRight:28, cursor:"pointer", display:"flex", alignItems:"center" }}
        onClick={() => onNavigate("dashboard")}>
        <img src={APP_CONFIG.logoPath} alt="logo" style={{ height:34, objectFit:"contain" }}/>
      </div>

      {/* Nav items */}
      <div className="flex flex-1 gap-4" style={{ overflowX:"auto", overflowY:"hidden" }}>
        {items.map(item => {
          const active = item.id === activeId;
          return (
            <button key={item.id} onClick={()=>onNavigate(item.id)} className={`nav-item${active?" active":""}`}>
              <span style={{ fontSize:16, display:"inline-flex", alignItems:"center" }}>{item.icon}</span>
              <span>{item.label}</span>
              {item.badge != null && item.badge > 0 && (
                <span style={{ background:"var(--green)", color:"var(--black)", fontSize:9, fontWeight:900, minWidth:16, height:16, borderRadius:8, display:"inline-flex", alignItems:"center", justifyContent:"center", padding:"0 4px" }}>{item.badge}</span>
              )}
              {active && <span className="nav-active-line"/>}
            </button>
          );
        })}
      </div>

      {/* Badge maintenance — au premier plan par rapport à la navbar */}
      {maintenanceBadge && (
        <button onClick={onMaintenanceBadgeClick}
          style={{
            position:"relative", zIndex:2, flexShrink:0,
            background:"#e8000e", color:"#fff", border:"none", borderRadius:8,
            cursor:"pointer", padding:"8px 14px 8px 10px", marginRight:8,
            animation:"maint-blink 5s ease-in-out infinite",
            fontSize:12, fontWeight:800, fontFamily:"var(--ff-title)",
            letterSpacing:0.6, textTransform:"uppercase",
            boxShadow:"0 2px 10px rgba(232,0,14,0.55)",
            display:"flex", alignItems:"center", gap:7, userSelect:"none",
          }}>
          <span style={{ fontSize:16, lineHeight:1 }}>🚧</span> Maintenance Active
        </button>
      )}

      {/* Profile */}
      <div ref={dropRef} className="relative shrink-0" style={{ marginLeft:16 }}>
        <button onClick={()=>setDropOpen(v=>!v)}
          style={{ display:"flex", alignItems:"center", gap:10, background:dropOpen?"rgba(0,255,0,0.1)":"rgba(255,255,255,0.05)", border:`1px solid ${dropOpen?"var(--green)":"rgba(255,255,255,0.1)"}`, borderRadius:8, padding:"5px 12px 5px 6px", cursor:"pointer", transition:"all 0.15s" }}>
          {user && <Avatar user={user} size={28}/>}
          <div style={{ textAlign:"left" }}>
            <div style={{ fontSize:12, fontWeight:600, color:"#fff", lineHeight:1.2, maxWidth:120, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{user?.name}</div>
          </div>
          {/* Indicator dot when role is overridden */}
          {isOverride && (
            <span style={{ width:7, height:7, borderRadius:"50%", background:"var(--gold)", flexShrink:0, marginLeft:-4 }}/>
          )}
          <svg width="10" height="6" viewBox="0 0 10 6" fill="none" style={{ transition:"transform 0.2s", transform:dropOpen?"rotate(180deg)":"none" }}>
            <path d="M1 1l4 4 4-4" stroke="#aaa" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        </button>

        {dropOpen && (
          <div className="dropdown">
            {/* Identity header */}
            <div style={{ padding:"16px 16px 12px", borderBottom:"1px solid #f0f0f0" }}>
              <div className="flex gap-12 mb-8">
                {user && <Avatar user={user} size={40}/>}
                <div>
                  <div className="t-title" style={{ fontSize:14, color:"var(--black)" }}>{user?.name}</div>
                  <div style={{ fontSize:11, color:"var(--text-muted)", marginTop:2 }}>{user?.email}</div>
                  <div style={{ marginTop:5, display:"flex", gap:4, flexWrap:"wrap" }}>
                    <RoleBadge role={role} isOverride={isOverride}/>
                  </div>
                </div>
              </div>
            </div>

            {/* Navigation */}
            <div style={{ padding:"6px 0" }}>
              <button className="drop-item" onClick={()=>{setDropOpen(false);onProfile();}}>👤 Mon profil</button>
              <button className="drop-item" onClick={()=>{setDropOpen(false);onNavigate("settings");}}>⚙️ Paramètres</button>
            </div>

            {/* Role switcher — admin / super-admin only */}
            {canSwitchRole && (
              <>
                <div className="divider"/>
                <div style={{ padding:"10px 14px 6px" }}>
                  <div style={{ fontSize:10, fontWeight:700, color:"var(--text-faint)", letterSpacing:1.2,
                    textTransform:"uppercase", marginBottom:8 }} className="t-title">
                    Simuler un rôle
                  </div>
                  <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>
                    {SWITCHABLE_ROLES.map(r => {
                      const isBase   = r === baseRole && !isOverride;
                      const active   = isBase ? r === role : role === r;
                      return (
                        <button key={r} onClick={()=>{
                          if (r === baseRole) overrideRole(null);
                          else overrideRole(r);
                        }}
                          style={{
                            fontSize:11, padding:"3px 10px", borderRadius:20, cursor:"pointer",
                            fontFamily:"var(--ff-title)", fontWeight:700, letterSpacing:0.3,
                            border: active
                              ? `1.5px solid ${isOverride && r !== baseRole ? "var(--gold)" : "var(--green)"}`
                              : "1.5px solid #e0e0e0",
                            background: active
                              ? (isOverride && r !== baseRole ? "var(--gold)" : "var(--green)")
                              : "transparent",
                            color: active
                              ? (isOverride && r !== baseRole ? "#1a1a2e" : "var(--black)")
                              : "var(--text-muted)",
                            transition:"all 0.12s",
                          }}>
                          {active && !isBase && "⚡ "}{ROLE_LABELS[r]}
                          {r === baseRole && <span style={{ fontSize:9, opacity:0.6, marginLeft:3 }}>(réel)</span>}
                        </button>
                      );
                    })}
                  </div>
                  {isOverride && (
                    <button onClick={()=>overrideRole(null)}
                      style={{ marginTop:8, fontSize:10, color:"var(--gold)", background:"none", border:"none",
                        cursor:"pointer", padding:0, textDecoration:"underline", fontFamily:"var(--ff-body)" }}>
                      ↩ Revenir au rôle réel ({ROLE_LABELS[baseRole]})
                    </button>
                  )}
                </div>
              </>
            )}

            <div className="divider"/>
            <div style={{ padding:"6px 0" }}>
              <button className="drop-item danger" onClick={()=>{setDropOpen(false);logout();}}>🚪 Déconnexion</button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
